import Client from "services/shopify-client";
import Cart from "models/cart-model";
import CartItem from "models/cart-item-model";
import ShopifyClient from "services/shopify-client";
import determineLineItems from "helpers/add-free-products";

const getExistingCheckoutId = () => localStorage.getItem("checkoutId");

const getOrCreateCheckoutId = async () => {
  const checkoutId = getExistingCheckoutId();
  if (checkoutId) return checkoutId;

  let newCheckout;
  if (window?.wgs?.customerReferenceId) {
    newCheckout = await ShopifyClient.checkout.create(
      {
        "customAttributes": [
          {
            "key": "_customerReferenceId",
            "value": window?.wgs?.customerReferenceId
          }
        ]
      }
    );
  } else {
    newCheckout = await ShopifyClient.checkout.create();
  }

  localStorage.setItem("checkoutId", newCheckout.id);

  return newCheckout.id;
};

// Fill and fetch the Cart Object
const fetchCart = async () => {
  const checkoutId = await getOrCreateCheckoutId();

  const checkout = await Client.checkout.fetch(checkoutId);
  const lineItems = checkout?.lineItems;
  const createdAt = new Date(checkout?.createdAt);
  const totalPriceV2 = checkout?.totalPriceV2;
  const webUrl = checkout?.webUrl;
  const hasDiscounts = checkout?.discountApplications.length !== 0;
  const discount = hasDiscounts ? checkout?.discountApplications[0] : null;
  let discountedAmount = 0;
  let totalSavings = 0; // compareAtPrice - price for all line items
  let totalQuantity = 0;
  let qtyPrice = 0;
  let qtyCompPrice = 0;
  let discountType;

  const items = lineItems?.map((item) => {
    const itemHasDiscounts = item.discountAllocations.length !== 0;
    let itemDiscount = {};
    let firstDiscount = null;
    let secondDiscount = null;
    let variantOptions = null;
    let compareAtPrice = item?.variant?.compareAtPriceV2?.amount ? parseFloat(item?.variant?.compareAtPriceV2?.amount) : 0;
    let originalPrice = parseFloat(item?.variant?.priceV2?.amount);
    let price = parseFloat(item?.variant?.priceV2?.amount);
    let saving = 0; // compareAtPrice - price
    qtyPrice = price * item.quantity;
    qtyCompPrice = compareAtPrice * item.quantity;
    totalQuantity += item?.quantity;
    let itemDiscountAmount;
    if (hasDiscounts && itemHasDiscounts) {
      firstDiscount = item?.discountAllocations[0];
      secondDiscount = item?.discountAllocations[1];
      if (secondDiscount === undefined) {
        discountType = firstDiscount;
      }
      else {
        discountType = secondDiscount;
      }
      itemDiscountAmount = firstDiscount?.allocatedAmount?.amount;
      if (itemDiscountAmount) discountedAmount += parseFloat(itemDiscountAmount);
      itemDiscount = {
        amount: firstDiscount?.allocatedAmount?.amount,
        code: firstDiscount?.discountApplication?.code,
        perc: firstDiscount?.discountApplication?.value?.percentage,
      };

      price = qtyPrice - parseFloat(itemDiscountAmount);
    }
    else {
      price = qtyPrice;
    }

    if (item.variant) {
      variantOptions = item.variant?.selectedOptions.map((option) => (
        {
          name: option.attrs.name.value,
          value: option.attrs.value.value,
        })
      );
    }

    if (compareAtPrice > originalPrice) {
      saving = qtyCompPrice - qtyPrice;
    }

    totalSavings += saving;

    const cartItem = new CartItem({
      id: item?.id,
      title: item?.title,
      productTitle: item?.title,
      quantity: item?.quantity,
      sku: item?.variant?.sku,
      variantTitle: item?.variant?.title,
      image: item?.variant?.image,
      available: item?.variant?.available,
      variantId: item?.variant?.id,
      productId: item?.variant?.product?.id,
      productHandle: item?.variant?.product?.handle,
      priceV2: item?.variant?.priceV2,
      qtyPrice,
      qtyCompPrice,
      price,
      compareAtPrice,
      compareAtPriceV2: item?.variant?.compareAtPriceV2,
      discount: itemDiscount,
      variantOptions,
      salePrice: price,
      saving
    });

    return cartItem;
  });

  const cart = new Cart({
    items: items,
    createdAt: createdAt,
    totalPriceV2: totalPriceV2,
    webUrl: webUrl,
    discount: {
      code: discount?.code,
      perc: discount?.value?.percentage,
      applicable: discount?.applicable,
    },
    totalQuantity: totalQuantity,
    discountedAmount: discountedAmount,
    hasDiscounts: hasDiscounts,
    discountName: discountType?.discountApplication?.code,
    totalSavings
  });
  return cart;
};

// Add to Cart
const addCartItem = async (variantId, price, cartSettings) => {
  const checkoutId = await getOrCreateCheckoutId();
  const cart = await fetchCart();

  const lineItem = determineLineItems(variantId, price, cart, cartSettings);

  const updatedCheckout = await Client.checkout.addLineItems(checkoutId, lineItem);
  return updatedCheckout;
};



const addCartItems = async (variantIds) => {
  const checkoutId = await getOrCreateCheckoutId();
  if (!checkoutId) return null;
  const lineItems = variantIds.map(id => {
    return {
      variantId: id,
      quantity: 1
    };
  });

  const updatedCheckout = await Client.checkout.addLineItems(checkoutId, lineItems);
  return updatedCheckout;
};

const addCartItemsWithCustomAttribute = async (variantIds, attributeKey, attributeValue) => {
  const checkoutId = await getOrCreateCheckoutId();
  if (!checkoutId) return null;

  const lineItems = variantIds.map(id => {
    return {
      variantId: id,
      quantity: 1,
      customAttributes: [
        { 
          key: attributeKey, 
          value: attributeValue 
        }
      ]
    };
  });

  const updatedCheckout = await Client.checkout.addLineItems(checkoutId, lineItems);
  return updatedCheckout;
};

// Add upsell item
const addUpsellItem = async (variantId) => {
  const checkoutId = await getOrCreateCheckoutId();

  const lineItem = [
    {
      variantId: variantId,
      quantity: 1,
    }
  ];

  const updatedCheckout = await Client.checkout.addLineItems(checkoutId, lineItem);
  return updatedCheckout;
};


// Update Cart Item
const updateCartItem = async (itemId, qty) => {
  const checkoutId = await getOrCreateCheckoutId();

  const lineItem = [{
    id: itemId,
    quantity: qty
  }];

  const updatedCheckout = await Client.checkout.updateLineItems(checkoutId, lineItem);
  return updatedCheckout;
};

// Remove Cart Item
const removeCartItems = async (lineItemIds) => {
  const checkoutId = await getOrCreateCheckoutId();

  const updatedCheckout = await Client.checkout.removeLineItems(checkoutId, lineItemIds);
  return updatedCheckout;
};

// Add discount
const addDiscount = async (discountCode, freeProduct) => {
  const cart = await fetchCart();
  const discountCodeLowerCase = discountCode?.toLowerCase();

  const triggeredVariantsExist = freeProduct?.variants?.filter((variant) => {
    if (variant?._type === "triggeredProductsForFree") {
      const variantDiscountCodeLowerCase = variant?.discountCode.toLowerCase();
      const hasMatchingVariant = variant?.triggeredVariants?.some((triggeredVariantId) => {
        return cart?.items?.some((cartItem) => btoa(cartItem?.variantId) === triggeredVariantId?.id);
      });

      return hasMatchingVariant && (discountCodeLowerCase === variantDiscountCodeLowerCase);
    }
  });

  if (triggeredVariantsExist?.length > 0) {
    const cartItems = [];

    for (const variant of freeProduct?.variants || []) {
      if (variant?._type === "triggeredProductsForFree") {
        const variantDiscountCodeLowerCase = variant?.discountCode.toLowerCase();
        if (discountCodeLowerCase === variantDiscountCodeLowerCase) {
          variant?.freeVariants?.forEach((freeVariant) => {
            cartItems.push(freeVariant?.id);
          });
        }
      }
    }

    await addCartItems(cartItems);
  }


  else {
    const matchingVariants = freeProduct?.variants?.filter((item) => {
      const variantDiscountCodeLowerCase = item?.discountCode.toLowerCase();
      return discountCodeLowerCase === variantDiscountCodeLowerCase && cart?.totalPriceV2?.amount >= item?.amountForFreeGift;
    });

    for (const variant of matchingVariants || []) {
      await addCartItem(variant?.freeVariant?.id);
    }
  }

  const checkoutId = await getOrCreateCheckoutId();

  const updatedDiscount = await Client.checkout.addDiscount(checkoutId, discountCode);
  return updatedDiscount;
};


// Remove discount
const removeDiscount = async () => {
  const checkoutId = await getOrCreateCheckoutId();

  const updatedDiscount = await Client.checkout.removeDiscount(checkoutId);
  return updatedDiscount;
};

export {
  fetchCart,
  addCartItem,
  addCartItems,
  addUpsellItem,
  updateCartItem,
  removeCartItems,
  addDiscount,
  removeDiscount,
  addCartItemsWithCustomAttribute
};